import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Play, Music, Radio, Image, Newspaper, Star, Users, Eye, TrendingUp, Headphones, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import ShareButton from '../components/ShareButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const { theme, isDarkMode, isCustomTheme, customSettings } = useTheme();
  const [stats, setStats] = useState({ total_visits: 0, today_visits: 0, page_views: {} });
  const [radioListeners, setRadioListeners] = useState(0);
  const [newMusic, setNewMusic] = useState([]);
  const [news, setNews] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteConfig, setSiteConfig] = useState({
    site_title: "BeatLab52",
    site_subtitle: "DJ808 & CRG Productionz",
    site_tagline: "Where Beats Come Alive",
    hero_background_url: "https://images.unsplash.com/photo-1550524756-d07d631274cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxyZWNvcmRpbmclMjBzdHVkaW8lMjBlcXVpcG1lbnQlMjBtaXhpbmclMjBjb25zb2xlJTIwZGFya3xlbnwwfHx8fDE3NzE4MTYxNzN8MA&ixlib=rb-4.1.0&q=85"
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Record visit
        await axios.post(`${API}/stats/visit`, null, { params: { page: 'home' } });
        
        // Fetch all data
        const [statsRes, musicRes, newsRes, photosRes, radioRes, configRes, videosRes] = await Promise.all([
          axios.get(`${API}/stats/visitor`),
          axios.get(`${API}/music/new?limit=8`),
          axios.get(`${API}/news?limit=8`),
          axios.get(`${API}/photos?limit=8`),
          axios.get(`${API}/radio/metadata`),
          axios.get(`${API}/site-config`),
          axios.get(`${API}/videos/featured`).catch(() => ({ data: [] })),
        ]);
        
        setStats(statsRes.data);
        setNewMusic(musicRes.data);
        setNews(newsRes.data);
        setPhotos(photosRes.data);
        setFeaturedVideos(videosRes.data);
        setRadioListeners(radioRes.data?.listeners || radioRes.data?.currentlisteners || 0);
        if (configRes.data) setSiteConfig(configRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // Poll radio listeners every 30 seconds
    const radioInterval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/radio/metadata`);
        setRadioListeners(res.data?.listeners || res.data?.currentlisteners || 0);
      } catch (e) {}
    }, 30000);

    return () => clearInterval(radioInterval);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const GlassCard = ({ children, className = '' }) => (
    <div 
      className={`backdrop-blur-xl rounded-xl p-6 transition-all duration-300 ${className}`}
      style={{ 
        backgroundColor: 'var(--bg-card)',
        borderColor: 'var(--border-color)',
        border: '1px solid var(--border-color)',
      }}
    >
      {children}
    </div>
  );

  return (
    <div className="-mt-20 sm:-mt-24">
      {/* Hero Section */}
      <section 
        className="relative min-h-[70vh] sm:min-h-[80vh] flex items-center justify-center overflow-hidden pt-20 sm:pt-24"
        style={{
          backgroundImage: `url(${(isCustomTheme && customSettings.heroImage) ? customSettings.heroImage : siteConfig.hero_background_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0" style={{
          background: isCustomTheme
            ? `linear-gradient(to bottom, rgba(0,0,0,${((customSettings.heroOverlayOpacity ?? 70) / 100 * 0.9).toFixed(2)}), rgba(0,0,0,${((customSettings.heroOverlayOpacity ?? 70) / 100 * 0.7).toFixed(2)}), rgba(0,0,0,${Math.min((customSettings.heroOverlayOpacity ?? 70) / 100 + 0.1, 1).toFixed(2)}))`
            : 'linear-gradient(to bottom, rgba(0,0,0,0.8), rgba(0,0,0,0.6), rgba(0,0,0,0.95))'
        }} />
        
        <motion.div 
          className="relative z-10 text-center px-4 max-w-4xl w-full"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-wider mb-2 sm:mb-4"
            style={{ 
              color: theme.primary,
              textShadow: theme.glow,
            }}
            data-testid="hero-title"
          >
            {siteConfig.site_title}
          </motion.h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-1 sm:mb-2" style={{ color: 'var(--text-secondary)' }}>
            {siteConfig.site_subtitle}
          </p>
          <p className="text-sm sm:text-base md:text-lg mb-6 sm:mb-8" style={{ color: 'var(--text-muted)' }}>
            {siteConfig.site_tagline}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8 px-4 sm:px-0">
            <Link to="/music" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                className="w-full sm:w-auto font-bold uppercase tracking-wider"
                style={{ backgroundColor: theme.primary, color: '#000' }}
                data-testid="explore-music-btn"
              >
                <Music className="w-5 h-5 mr-2" />
                Explore Music
              </Button>
            </Link>
            <Link to="/radio" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                variant="outline"
                className="w-full sm:w-auto font-bold uppercase tracking-wider"
                style={{ borderColor: theme.secondary, color: theme.secondary }}
                data-testid="live-radio-btn"
              >
                <Radio className="w-5 h-5 mr-2" />
                Live Radio
              </Button>
            </Link>
            <div className="hidden sm:block">
              <ShareButton 
                title="BeatLab52 - DJ808 & CRG Productionz"
                text="Check out BeatLab52 - The ultimate music platform by DJ808 & CRG Productionz!"
                className="font-bold uppercase tracking-wider"
              />
            </div>
          </div>

          {/* Stats Row */}
          <div className="flex flex-wrap gap-3 sm:gap-6 justify-center px-2">
            <motion.div 
              className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-sm sm:text-base"
              style={{ backgroundColor: 'rgba(0,0,0,0.6)', border: `1px solid ${theme.primary}40` }}
              whileHover={{ scale: 1.05 }}
            >
              <Eye className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: theme.primary }} />
              <span className="font-bold" style={{ color: theme.primary }}>{stats.total_visits.toLocaleString()}</span>
              <span className="hidden xs:inline" style={{ color: 'var(--text-muted)' }}>Visitors</span>
            </motion.div>
            <motion.div 
              className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-sm sm:text-base"
              style={{ backgroundColor: 'rgba(0,0,0,0.6)', border: `1px solid ${theme.secondary}40` }}
              whileHover={{ scale: 1.05 }}
            >
              <Headphones className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: theme.secondary }} />
              <span className="font-bold" style={{ color: theme.secondary }}>{radioListeners}</span>
              <span className="hidden xs:inline" style={{ color: 'var(--text-muted)' }}>Listeners</span>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Content Sections */}
      <motion.div 
        className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-8 sm:space-y-12"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* New Music Section */}
        <motion.section variants={itemVariants}>
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ color: theme.primary }}>
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6" />
              New Music
            </h2>
            <Link to="/music">
              <Button variant="ghost" size="sm" style={{ color: theme.primary }}>
                View All →
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
            {newMusic.length > 0 ? newMusic.slice(0, 8).map((track) => (
              <Link to={`/music?track=${track.id}`} key={track.id}>
                <GlassCard className="group cursor-pointer hover:scale-105 transition-transform p-3 sm:p-4">
                  <div className="aspect-square rounded-lg overflow-hidden mb-2 sm:mb-3 bg-black/20">
                    {track.cover_image ? (
                      <img src={track.cover_image} alt={track.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="w-8 h-8 sm:w-12 sm:h-12" style={{ color: theme.primary }} />
                      </div>
                    )}
                  </div>
                  <h3 className="font-bold truncate text-xs sm:text-sm" style={{ color: 'var(--text-primary)' }}>{track.title}</h3>
                  <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{track.artist || 'BeatLab52'}</p>
                </GlassCard>
              </Link>
            )) : (
              <GlassCard className="col-span-full text-center py-8 sm:py-12">
                <Music className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4" style={{ color: theme.primary }} />
                <p style={{ color: 'var(--text-muted)' }}>New music coming soon!</p>
              </GlassCard>
            )}
          </div>
        </motion.section>

        {/* Featured Videos Section */}
        {featuredVideos.length > 0 && (
          <motion.section variants={itemVariants}>
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ color: theme.secondary }}>
                <Video className="w-5 h-5 sm:w-6 sm:h-6" />
                Featured Videos
              </h2>
              <Link to="/videos">
                <Button variant="ghost" size="sm" style={{ color: theme.secondary }}>
                  View All →
                </Button>
              </Link>
            </div>
            <div className={`grid gap-3 sm:gap-4 ${featuredVideos.length === 1 ? 'grid-cols-1 max-w-3xl mx-auto' : featuredVideos.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : featuredVideos.length === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
              {featuredVideos.map((video) => {
                const url = video.video_url || '';
                let embedUrl = null;
                if (url.includes('youtube.com') || url.includes('youtu.be')) {
                  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
                  if (match) embedUrl = `https://www.youtube.com/embed/${match[1]}`;
                } else if (url.includes('vimeo.com')) {
                  const match = url.match(/vimeo\.com\/(\d+)/);
                  if (match) embedUrl = `https://player.vimeo.com/video/${match[1]}`;
                }
                return (
                  <GlassCard key={video.id} className="overflow-hidden p-0">
                    <div className="aspect-video">
                      {embedUrl ? (
                        <iframe
                          src={embedUrl}
                          title={video.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ border: 'none' }}
                        />
                      ) : (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="block w-full h-full relative">
                          {video.thumbnail ? (
                            <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-black/40">
                              <Play className="w-12 h-12" style={{ color: theme.primary }} />
                            </div>
                          )}
                        </a>
                      )}
                    </div>
                    <div className="p-3 sm:p-4">
                      <h3 className="font-bold text-sm sm:text-base truncate" style={{ color: 'var(--text-primary)' }}>{video.title}</h3>
                      {video.category && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{video.category}</p>}
                    </div>
                  </GlassCard>
                );
              })}
            </div>
          </motion.section>
        )}

        {/* News Section */}
        <motion.section variants={itemVariants}>
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ color: theme.secondary }}>
              <Newspaper className="w-5 h-5 sm:w-6 sm:h-6" />
              Latest News
            </h2>
            <Link to="/news">
              <Button variant="ghost" size="sm" style={{ color: theme.secondary }}>
                View All →
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {news.length > 0 ? news.slice(0, 6).map((item) => (
              <Link to={`/news/${item.id}`} key={item.id}>
                <GlassCard className="group cursor-pointer hover:scale-[1.02] transition-transform h-full overflow-hidden">
                  {item.image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-3 sm:p-4">
                    <span className="text-[10px] uppercase tracking-wider font-medium" style={{ color: theme.secondary }}>{item.category || 'News'}</span>
                    <h3 className="font-bold mt-1 line-clamp-2 text-sm group-hover:underline" style={{ color: 'var(--text-primary)' }}>{item.title}</h3>
                    <p className="text-xs mt-1.5 line-clamp-2" style={{ color: 'var(--text-muted)' }}>{item.content?.slice(0, 100)}...</p>
                  </div>
                </GlassCard>
              </Link>
            )) : (
              <GlassCard className="col-span-full text-center py-8 sm:py-12">
                <Newspaper className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4" style={{ color: theme.secondary }} />
                <p style={{ color: 'var(--text-muted)' }}>Stay tuned for the latest news!</p>
              </GlassCard>
            )}
          </div>
        </motion.section>

        {/* Photo Gallery Preview */}
        <motion.section variants={itemVariants}>
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ color: theme.accent }}>
              <Image className="w-5 h-5 sm:w-6 sm:h-6" />
              Photo Gallery
            </h2>
            <Link to="/gallery">
              <Button variant="ghost" size="sm" style={{ color: theme.accent }}>
                View All →
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1 sm:gap-2">
            {photos.length > 0 ? photos.slice(0, 8).map((photo) => (
              <Link to={`/gallery?photo=${photo.id}`} key={photo.id}>
                <div className="aspect-square rounded-lg overflow-hidden group cursor-pointer">
                  <img 
                    src={photo.image_url} 
                    alt={photo.title} 
                    className="w-full h-full object-cover transition-transform group-hover:scale-110"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              </Link>
            )) : (
              <GlassCard className="col-span-full text-center py-8 sm:py-12">
                <Image className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4" style={{ color: theme.accent }} />
                <p style={{ color: 'var(--text-muted)' }}>Photo gallery coming soon!</p>
              </GlassCard>
            )}
          </div>
        </motion.section>

        {/* Quick Links */}
        <motion.section variants={itemVariants}>
          <GlassCard className="p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-center" style={{ color: 'var(--text-primary)' }}>
              Connect With Us
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
              <a href="https://www.youtube.com/@BEATLAB52MR808" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2 p-3 sm:p-4 rounded-lg hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FF0000' }}>
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </div>
                <span className="text-xs sm:text-sm font-medium" style={{ color: 'var(--text-primary)' }}>YouTube</span>
              </a>
              <a href="https://www.instagram.com/dj808601/" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2 p-3 sm:p-4 rounded-lg hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}>
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </div>
                <span className="text-xs sm:text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Instagram</span>
              </a>
              <a href="https://www.facebook.com/Dj808601/" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2 p-3 sm:p-4 rounded-lg hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1877F2' }}>
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </div>
                <span className="text-xs sm:text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Facebook</span>
              </a>
              <Link to="/about" className="flex flex-col items-center gap-2 p-3 sm:p-4 rounded-lg hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.primary }}>
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                </div>
                <span className="text-xs sm:text-sm font-medium" style={{ color: 'var(--text-primary)' }}>About Us</span>
              </Link>
            </div>
          </GlassCard>
        </motion.section>
      </motion.div>
    </div>
  );
};

export default Home;
